import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/components/staff/Panel";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/i18n";

function FixPhone({ companyId, onDone }) {
  const [v, setV] = useState("");
  const save = () => api.put(`/companies/${companyId}`, { contact_phone: v.trim() }).then(() => { toast.success("Company phone saved"); onDone(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="flex gap-2" data-testid="wa-fix-phone">
      <Input placeholder="e.g. 07XX XXX XXX or +255…" value={v} onChange={(e) => setV(e.target.value)} data-testid="wa-fix-phone-input" />
      <Button size="sm" variant="outline" disabled={!v.trim()} onClick={save} data-testid="wa-fix-phone-save">Save number</Button>
    </div>
  );
}

function Numbers({ d, src, pick }) {
  return (
    <div className="space-y-1" data-testid="wa-numbers">{d.numbers.map((n) => (
      <label key={n.source} className="flex cursor-pointer items-start gap-2 rounded-md border p-2 text-sm">
        <input type="radio" checked={src === n.source} onChange={() => pick(n.source)} data-testid={`wa-number-${n.source}`} className="mt-1" />
        <span><b>{n.label}</b><br /><span className={n.number ? "text-muted-foreground" : "text-red-600"} data-testid={`wa-number-status-${n.source}`}>{n.number ? `+${n.number}` : `${n.raw ? `${n.raw}: ` : ""}${n.error}`}</span></span>
      </label>))}</div>
  );
}

function TemplatePick({ tpls, tid, setTid }) {
  if (!tpls.length) return <p className="text-xs text-muted-foreground" data-testid="wa-no-templates">No active templates for this record. Write your message below.</p>;
  return (
    <select className="w-full rounded-md border bg-background p-2 text-sm" value={tid || ""} onChange={(e) => setTid(e.target.value)} data-testid="wa-template-select">
      {tpls.map((t) => <option key={t.id} value={t.id} disabled={!!t.missing.length}>{t.name}{t.missing.length ? ` (needs: ${t.missing.join(", ")})` : t.applies ? " · suggested" : ""}</option>)}
    </select>
  );
}

function Composer({ kind, id, d, reload, onClose, lang }) {
  const { t } = useLang();
  const first = d.numbers.find((n) => n.number) || d.numbers[0];
  const firstTpl = d.templates.find((t) => !t.missing.length);
  const [src, setSrc] = useState(first?.source);
  const [tid, setTid] = useState(firstTpl?.id);
  const textFor = (t, s) => d.templates.find((x) => x.id === t)?.messages?.[s] || "";
  const [msg, setMsg] = useState(textFor(firstTpl?.id, first?.source));
  const cur = d.numbers.find((n) => n.source === src);
  const pickSrc = (s) => { setSrc(s); if (tid) setMsg(textFor(tid, s)); };
  const pickTpl = (t) => { setTid(t); setMsg(textFor(t, src)); };
  const open = () => api.post("/whatsapp/open", { kind, id, source: src, template_id: tid || null, lang, message: msg }).then(({ data }) => {
    window.open(data.url, "_blank", "noopener,noreferrer");
    toast.info("WhatsApp opened with your draft. It is NOT sent until you press Send in WhatsApp.");
    onClose();
  }).catch((e) => toast.error(errMsg(e)));
  if (!cur) return <p className="text-sm text-muted-foreground">No contact is available for this record.</p>;
  return (
    <div className="space-y-3">
      <Numbers d={d} src={src} pick={pickSrc} />
      {!cur.number && cur.source === "company" && <FixPhone companyId={d.company_id} onDone={reload} />}
      {!cur.number && cur.source !== "company" && <p className="text-xs text-muted-foreground">Add this person's WhatsApp number in their user record (Access), or ask them to add it under Account.</p>}
      <TemplatePick tpls={d.templates} tid={tid} setTid={pickTpl} />
      {lang === "sw" && <p className="text-xs text-amber-800" data-testid="wa-sw-review-note">{t("Kiswahili templates are pending expert review. Check the wording before opening WhatsApp.")}</p>}
      <Textarea rows={7} value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={2000} data-testid="wa-message" />
      <p className="text-xs text-muted-foreground">Check the text before opening. Don't add passwords, internal notes or confidential details.</p>
      <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid="wa-not-sent-note">{t("Opening WhatsApp only prepares the message. Nothing is sent until you press Send inside WhatsApp, and the system does not record it as sent.")}</p>
      <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose} data-testid="wa-cancel">Cancel</Button>
        <Button disabled={!cur.number || !msg.trim()} onClick={open} className="bg-[#128C7E] text-white hover:bg-[#0e6f64]" data-testid="wa-open-btn"><MessageCircle className="mr-1 h-4 w-4" /> {t("Open WhatsApp")}</Button></div>
    </div>
  );
}

export function WhatsAppButton({ kind, id, size = "sm", label = "Send WhatsApp Message", variant = "outline" }) {
  const { user, isStaff } = useAuth();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const { t, lang: uiLang } = useLang();
  const [lang, setLang] = useState(uiLang);
  if (!isStaff || !can(user, "whatsapp_contact")) return null;
  const load = (l = lang) => api.get("/whatsapp/draft", { params: { kind, id, lang: l } }).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); setOpen(false); });
  const start = () => { setD(null); setOpen(true); setLang(uiLang); load(uiLang); };
  const switchLang = (l) => { setLang(l); setD(null); load(l); };
  return (<>
    <Button size={size} variant={variant} onClick={start} data-testid={`wa-btn-${kind}-${id}`}><MessageCircle className="mr-1 h-4 w-4 text-[#128C7E]" /> {label}</Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="wa-dialog">
        <DialogHeader><DialogTitle>{t("Send WhatsApp Message")}</DialogTitle><DialogDescription>{d ? d.company_name : t("Preparing the message…")}</DialogDescription></DialogHeader>
        <div className="flex items-center gap-2 text-xs" data-testid="wa-lang"><span className="text-muted-foreground">{t("Message language")}:</span>
          {[["en", "English"], ["sw", "Kiswahili"]].map(([k, l]) => <button key={k} type="button" onClick={() => lang !== k && switchLang(k)} aria-pressed={lang === k} className={`rounded-full border px-2.5 py-1 ${lang === k ? "bg-[#00A9D7] text-black" : ""}`} data-testid={`wa-lang-${k}`}>{l}</button>)}</div>
        {d && <Composer key={lang + JSON.stringify(d.numbers.map((n) => n.raw))} lang={lang} kind={kind} id={id} d={d} reload={() => load()} onClose={() => setOpen(false)} />}
      </DialogContent>
    </Dialog>
  </>);
}
